/**
 * ListCard — card grid view for master entity list.
 *
 * Renders the same data as ListTable but in a responsive card grid.
 * Field layout within each card is driven by the `cardPosition` property
 * on each field definition:
 *   - `'title'`    → large title in the CardHeader (first matching field wins)
 *   - `'subtitle'` → secondary text below the title
 *   - `'body'`     → shown as label/value pairs in CardContent (default)
 *   - `'footer'`   → shown at the bottom of the card
 *
 * When no field has `cardPosition` configured (backward compat), the first
 * `showInList` field becomes the title and the rest go to the body.
 *
 * @module components/list/ListCard
 */
import React, { useMemo } from 'react';
import { Building2, Globe } from 'lucide-react';
import type { MasterPatternViewDefinition } from '../../types/definition.types';
import type { MasterPatternViewEntity } from '../../types/service.types';
import type { MasterPatternViewPermissions } from '../../types/permissions.types';
import type { MasterPatternViewCustomAction, MasterPatternViewDefaultActions } from '../../types/props.types';
import { getAllFields } from '../../engine/fieldEngine';
import { useFieldEngine } from '../../hooks/useFieldEngine';
import { RowActions } from './RowActions';
import type { ToastCallback } from '../../hooks/useToast';
import {
  Card,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
  CardFooter,
} from '../../../Card/Card';

export interface ListCardProps<T extends MasterPatternViewEntity = MasterPatternViewEntity> {
  definition: MasterPatternViewDefinition;
  data: T[];
  onOpenView: (row: T) => void;
  onOpenEdit: (row: T) => void;
  onChangeStatus?: (row: T) => void;
  isLoading?: boolean;
  permissions?: MasterPatternViewPermissions;
  actions?: MasterPatternViewCustomAction<T>[];
  rowActions?: (row: T) => React.ReactNode;
  toast?: ToastCallback;
  showInternalToasts?: boolean;
  /** Active company context — undefined or 'global' means global view */
  activeCompanyContext?: string;
  /**
   * When provided, a per-card company-enable badge renders next to the title (mirrors
   * MasterCrud's showCompanyStatusColumn) — only while a specific company is active.
   * Clicking it opens the same "Habilitar por compañía" panel as the Acciones-column
   * action does in global scope.
   */
  onCompanyStatusClick?: (row: T) => void | Promise<void>;
  /** When true, disables pointer events to prevent interaction through modal overlays */
  modalOpen?: boolean;
  defaultActions?: MasterPatternViewDefaultActions<T>;
  /**
   * Custom render function for card content.
   * When provided, replaces the auto-generated CardContent body.
   * Receives the row data and its zero-based index.
   */
  renderCardContent?: (row: T, index: number) => React.ReactNode;
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

/** Format a raw field value for display inside a card. */
function formatValue(
  value: unknown,
  dataType: string,
  tt: (key: string, defaultValue?: string) => string,
): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-content-tertiary dark:text-dark-content-tertiary">—</span>;
  }
  if (dataType === 'boolean' || typeof value === 'boolean') {
    return value ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-[#ecfccb] text-[#4d7c0f]">
        {tt('active', 'Activo')}
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-background-secondary dark:bg-dark-bg-secondary text-content-tertiary dark:text-dark-content-tertiary">
        {tt('inactive', 'Inactivo')}
      </span>
    );
  }
  return String(value);
}

/** Skeleton placeholder for a single card while loading. */
function CardSkeleton() {
  return (
    <div className="animate-pulse rounded-xl border border-border-primary dark:border-dark-border-primary bg-bg-primary dark:bg-dark-bg-secondary p-6 flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2 flex-1">
          <div className="h-4 bg-background-secondary dark:bg-dark-bg-primary rounded w-2/3" />
          <div className="h-3 bg-background-secondary dark:bg-dark-bg-primary rounded w-1/2" />
        </div>
        <div className="flex gap-1 ml-4">
          <div className="w-7 h-7 rounded-md bg-background-secondary dark:bg-dark-bg-primary" />
          <div className="w-7 h-7 rounded-md bg-background-secondary dark:bg-dark-bg-primary" />
          <div className="w-7 h-7 rounded-md bg-background-secondary dark:bg-dark-bg-primary" />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="h-3 bg-background-secondary dark:bg-dark-bg-primary rounded w-full" />
        <div className="h-3 bg-background-secondary dark:bg-dark-bg-primary rounded w-4/5" />
        <div className="h-3 bg-background-secondary dark:bg-dark-bg-primary rounded w-3/5" />
      </div>
    </div>
  );
}

export function ListCard<T extends MasterPatternViewEntity = MasterPatternViewEntity>({
  definition,
  data,
  onOpenView,
  onOpenEdit,
  onChangeStatus,
  isLoading = false,
  permissions,
  actions,
  rowActions,
  toast: toastProp,
  showInternalToasts = true,
  activeCompanyContext,
  onCompanyStatusClick,
  modalOpen = false,
  renderCardContent,
  defaultActions,
  t,
}: ListCardProps<T>) {
  const tt = useMemo(
    () => t ?? ((key: string, defaultValue?: string) => defaultValue ?? key),
    [t],
  );
  const isCompanyCtx = !!activeCompanyContext && activeCompanyContext !== 'global';
  const { listColumns } = useFieldEngine(definition);

  // Build a map from fieldName → full field definition (for cardPosition, dataType, label, etc.)
  const fieldMap = useMemo(
    () => new Map(getAllFields(definition).map((f) => [f.fieldName, f])),
    [definition],
  );

  // Derive which fields go where in the card.
  // Only consider fields that are present in listColumns (i.e. showInList !== false)
  // and where showInCard !== false.
  //
  // Body columns render in field order — boolean fields (no custom renderCell) render
  // as an Activo/Inactivo badge instead of a "Label: Valor" line, but stay in place
  // among the other fields (matches MasterCRUDCard.tsx:220-231: the status badge is
  // just another entry in `contentFields.map(...)`, not hoisted to the header).
  const { titleCol, subtitleCol, bodyColumns, footerColumns } = useMemo(() => {
    const visibleCols = listColumns.filter((col) => {
      const fd = fieldMap.get(col.fieldName);
      return fd?.showInCard !== false;
    });

    const anyHasPosition = visibleCols.some((col) => {
      const fd = fieldMap.get(col.fieldName);
      return !!fd?.cardPosition;
    });

    let titleCol = null;
    let subtitleCol = null;
    let bodyColumns: typeof visibleCols = [];
    let footerColumns: typeof visibleCols = [];

    if (!anyHasPosition) {
      // Fallback: first field → title, rest → body
      const [first, ...rest] = visibleCols;
      titleCol = first ?? null;
      bodyColumns = rest;
    } else {
      titleCol =
        visibleCols.find((col) => fieldMap.get(col.fieldName)?.cardPosition === 'title') ?? null;
      subtitleCol =
        visibleCols.find((col) => fieldMap.get(col.fieldName)?.cardPosition === 'subtitle') ?? null;
      footerColumns = visibleCols.filter(
        (col) => fieldMap.get(col.fieldName)?.cardPosition === 'footer',
      );
      bodyColumns = visibleCols.filter((col) => {
        const pos = fieldMap.get(col.fieldName)?.cardPosition;
        return !pos || pos === 'body';
      });
    }

    return { titleCol, subtitleCol, bodyColumns, footerColumns };
  }, [listColumns, fieldMap]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4"
      style={{ pointerEvents: modalOpen ? 'none' : undefined }}
    >
      {data.map((row, index) => {
        const r = row as Record<string, unknown>;
        const overrides = r['_overrides'] as Record<string, unknown> | undefined;

        // Title value and company-context badge
        const titleValue = titleCol ? r[titleCol.fieldName] : null;
        const titleFd = titleCol ? fieldMap.get(titleCol.fieldName) : null;
        const isGlobal = isCompanyCtx && titleFd?.isOverridable;

        const titleNode = (
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <span>
              {titleFd?.renderCell
                ? (titleFd.renderCell(titleValue, row) as React.ReactNode)
                : titleValue !== null && titleValue !== undefined
                  ? String(titleValue)
                  : <span className="text-content-tertiary dark:text-dark-content-tertiary">—</span>
              }
            </span>
            {isGlobal && (
              overrides?.[titleCol!.fieldName] !== undefined ? (
                <span
                  title={tt('companyOverrideTooltip', 'Valor sobreescrito por la compañía')}
                  className="inline-flex items-center justify-center w-5 h-5 rounded-[4px] bg-[#eff8ff] border border-[#93d1fd] text-[#1355b7] flex-shrink-0 cursor-help"
                >
                  <Building2 size={12} />
                </span>
              ) : (
                <span
                  title={tt('companyGlobalValueTooltip', 'Valor global')}
                  className="inline-flex items-center justify-center w-5 h-5 rounded-[4px] bg-background-secondary dark:bg-dark-bg-secondary border border-border-primary dark:border-dark-border-primary text-content-tertiary dark:text-dark-content-tertiary flex-shrink-0 cursor-help"
                >
                  <Globe size={12} />
                </span>
              )
            )}
          </span>
        );

        const subtitleFd = subtitleCol ? fieldMap.get(subtitleCol.fieldName) : null;
        const subtitleValue = subtitleCol ? r[subtitleCol.fieldName] : null;

        return (
          <Card key={row.id} className="gap-4 py-4">
            <CardHeader className="px-4">
              <CardTitle className="text-sm">
                <div className="flex flex-col gap-0.5">
                  {titleNode}
                  {subtitleCol && subtitleValue !== null && subtitleValue !== undefined && (
                    <span className={`text-xs font-normal text-content-secondary dark:text-dark-content-secondary leading-none${titleFd?.renderCell ? ' pl-[1.125rem]' : ''}`}>
                      {subtitleFd?.renderCell
                        ? (subtitleFd.renderCell(subtitleValue, row) as React.ReactNode)
                        : String(subtitleValue)
                      }
                    </span>
                  )}
                </div>
              </CardTitle>
              <CardAction>
                <div className="flex items-center gap-2">
                  <RowActions<T>
                    row={row}
                    permissions={permissions}
                    onOpenView={onOpenView}
                    onOpenEdit={onOpenEdit}
                    onChangeStatus={onChangeStatus}
                    actions={actions}
                    rowActions={rowActions}
                    toast={toastProp}
                    showInternalToasts={showInternalToasts}
                    defaultActions={defaultActions}
                    variant="card"
                    t={tt}
                  />
                </div>
              </CardAction>
            </CardHeader>

            <CardContent className="px-4">
              {renderCardContent ? (
                renderCardContent(row, index)
              ) : (
                <div className="flex flex-col gap-1.5">
                  {/* Body fields in definition order — boolean fields (no custom renderCell)
                      render as a status badge instead of "Label: Valor", same as
                      MasterCRUDCard.tsx's contentFields loop. */}
                  {bodyColumns.map((col) => {
                    const fd = fieldMap.get(col.fieldName);
                    const value = r[col.fieldName];
                    const isStatusField = fd?.dataType === 'boolean' && !fd?.renderCell;

                    if (isStatusField) {
                      return (
                        <div key={col.fieldName} className="mt-1">
                          {formatValue(value, 'boolean', tt)}
                        </div>
                      );
                    }

                    const displayValue = fd?.renderCell
                      ? (fd.renderCell(value, row) as React.ReactNode)
                      : formatValue(value, fd?.dataType ?? 'string', tt);

                    return (
                      <div key={col.fieldName} className="flex items-baseline gap-1 text-xs min-w-0">
                        <span className="shrink-0 font-medium text-content-tertiary dark:text-dark-content-tertiary">
                          {col.label}:
                        </span>
                        <span className="text-content-primary dark:text-dark-content-primary truncate">
                          {displayValue}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>

            {footerColumns.length > 0 && (
              <CardFooter className="px-4 pt-0 gap-4 flex-wrap">
                {footerColumns.map((col) => {
                  const fd = fieldMap.get(col.fieldName);
                  const value = r[col.fieldName];
                  const displayValue = fd?.renderCell
                    ? (fd.renderCell(value, row) as React.ReactNode)
                    : formatValue(value, fd?.dataType ?? 'string', tt);
                  return (
                    <div key={col.fieldName} className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-medium text-content-tertiary dark:text-dark-content-tertiary uppercase tracking-wide">
                        {col.label}
                      </span>
                      <span className="text-xs text-content-primary dark:text-dark-content-primary">
                        {displayValue}
                      </span>
                    </div>
                  );
                })}
              </CardFooter>
            )}
          </Card>
        );
      })}
    </div>
  );
}
